import React, { useState, useEffect } from "react";
import { z } from "zod";
import { useStoredPreference } from "@/hooks/useStoredPreference";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components";
import { roleManagementApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { RolesTable } from "@/pages/Roles/RolesTable";
import { NewRoleModal } from "@/pages/Roles/NewRoleModal";
import { Alert } from "@/components/Alert";

import type { RoleItem } from "@/pages/Roles/types";

export const Roles: React.FC = () => {
    const { t } = useTranslation("global");
    const { isAdmin } = useAuth();
    const { showError } = useToast();
    const [items, setItems] = useState<RoleItem[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [search, setSearch] = useStoredPreference(
        "vigepra_roles_search",
        z.string().max(500),
        "",
        true
    );
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isNewModalOpen, setIsNewModalOpen] = useState<boolean>(false);

    const activeCompanyId = localStorage.getItem("vigepra_active_company");
    const isMainCompany = !activeCompanyId || activeCompanyId === "main";

    const fetchItems = async () => {
        setLoading(true);
        setErrorMessage(null);
        try {
            const res = await roleManagementApi.getAll();
            if (res.result) {
                setItems(res.data);
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : t("common.error");
            setErrorMessage(msg);
            showError(msg);
            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!isMainCompany) {
            fetchItems();
        }
    }, [isMainCompany, activeCompanyId]);

    const handleUpdateItem = (updated: RoleItem) => {
        setItems((prev) =>
            prev.map((item) =>
                item.id === updated.id ? { ...item, ...updated } : item
            )
        );
    };

    const handleDeleteItem = (deletedId: number) => {
        setItems((prev) => prev.filter((item) => item.id !== deletedId));
    };

    const displayedItems = items.filter((item: RoleItem) => {
        if (search) {
            const s = search.toLowerCase();
            const name = item.name?.toLowerCase() || "";
            const tag = item.tag?.toLowerCase() || "";
            const desc = item.description?.toLowerCase() || "";

            if (!name.includes(s) && !tag.includes(s) && !desc.includes(s)) {
                return false;
            }
        }
        return true;
    });

    return (
        <div className="page">
            {isMainCompany ? (
                <div className="p-8 text-center text-slate-500">
                    {t("roles.roles_only_tenant")}
                </div>
            ) : (
                <>
                    <PageHeader
                        compact
                        className="!mb-0"
                        title={<span>{t("roles.title")}</span>}
                        action={
                            isAdmin
                                ? {
                                      label: t("roles.new_button"),
                                      icon: "plusCircle",
                                      onClick: () => setIsNewModalOpen(true),
                                  }
                                : null
                        }
                    />

                    <Alert
                        variant="warning"
                        text={t("roles.disclaimer")}
                        className="mb-4"
                        align="center"
                    />

                    <RolesTable
                        items={displayedItems}
                        loading={loading}
                        error={errorMessage}
                        search={search}
                        onSearchChange={setSearch}
                        onUpdateItem={handleUpdateItem}
                        onDeleteItem={handleDeleteItem}
                        onRefresh={fetchItems}
                    />

                    <NewRoleModal
                        isOpen={isNewModalOpen}
                        onClose={() => setIsNewModalOpen(false)}
                        onSuccess={() => fetchItems()}
                    />
                </>
            )}
        </div>
    );
};
